import Match from 'app/components/tv/models/Match';
import {ImageInterface} from 'app/types/ImageInterface';
import {UserResource} from 'app/components/tv/apiResources/UserResource';
import {ApiVideo} from 'app/components/tv/apiResources/apiVideo';
import {VideoSources, VideoSourceType} from 'app/components/tv/models/VideoSources';
import {captureSentryException} from 'app/services/sentryLogging';
import is from '@sindresorhus/is';
import {Iso8601DateString} from 'app/types/Iso8601DateString';

interface VideoInterface {
  id: number;
  slug: string;
  category: string;
  user: UserResource | null;
  title: string;
  matchPlaylist: string;
  matchPlaylistId: number;
  source: string;
  startSec: number | null;
  duration: number;
  dateCreated: Iso8601DateString;
  thumbnail: ImageInterface;
  minute: number;
  additionalMinute: number;
  homeGoal: number | null;
  guestGoal: number | null;
  actionType: string;
  muted: boolean;
  match: Match | null;
  sources: {type: string; src: string}[];
  poster: string;
  get startTime(): number;
  get videoDuration(): number | null;
  getPlayedTime(timeRanges: TimeRanges): number;
  isSeekable(): boolean;
  manuallyEndAt(): number | null;
  showFuPaTvLogo(): boolean;
}

export default abstract class Video implements VideoInterface {
  readonly id: number;
  readonly slug: string;
  readonly category: string;
  readonly user: UserResource | null;
  readonly title: string;
  readonly matchPlaylist: string;
  readonly matchPlaylistId: number;
  readonly source: string;
  readonly startSec: number | null;
  readonly duration: number;
  readonly dateCreated: Iso8601DateString;
  readonly thumbnail: ImageInterface;
  readonly minute: number;
  readonly additionalMinute: number;
  readonly homeGoal: number | null;
  readonly guestGoal: number | null;
  readonly actionType: string;
  readonly muted: false;
  readonly match: Match | null;
  readonly sources: VideoSources;
  readonly poster: string;

  constructor(data: ApiVideo) {
    Object.assign(this, data);
    const sourceType: VideoSourceType = this.source?.endsWith('m3u8') ? 'application/x-mpegURL' : 'video/mp4';
    this.sources = [{type: sourceType, src: this.source}];
    this.poster = this.thumbnail ? `${this.thumbnail.path}1440x810.jpeg` : '';
    if (!data.match) {
      captureSentryException(new Error(`No match found for video`), {extra: {id: this.id, slug: this.slug}});
      this.match = null;
    } else {
      this.match = new Match(data.match);
    }
  }

  abstract get startTime(): number;
  abstract get videoDuration(): number | null;
  abstract isSeekable(): boolean;
  abstract showFuPaTvLogo(): boolean;

  /**
   * @returns null if the video should not end manually, otherwise the time in seconds when the video should end
   */
  abstract manuallyEndAt(): number | null;

  getPlayedTime(timeRanges: TimeRanges): number {
    return Math.floor(this.calculatePlayedTime(timeRanges));
  }

  private calculatePlayedTime = (playedTimeRanges: TimeRanges) => {
    let playedTime = 0;
    for (let index = 0; index < playedTimeRanges.length; index++) {
      const playedTimeRange = playedTimeRanges.end(index) - playedTimeRanges.start(index);
      playedTime += playedTimeRange;
    }
    // first time-range always starts at 0, even when setting startTime
    if (this.startTime && playedTime >= this.startTime) {
      return playedTime - this.startTime;
    }
    return playedTime;
  };

  getRegionSlug(): string {
    const matchRegionSlug = this.match?.getRegionSlug();
    if (matchRegionSlug) {
      return matchRegionSlug;
    }
    if (this.user?.district.region.slug) {
      return this.user.district.region.slug;
    }
    captureSentryException(new Error('No region slug found for video'), {match: this.match, user: this.user});
    return 'global';
  }

  getTitleWithMinute(): string {
    const minute = this.additionalMinute > 0 ? `${this.minute} +${this.additionalMinute}` : this.minute;
    return `${this.title} - ${minute}. Spielminute`;
  }

  getMatchTitle(): string | null {
    if (!this.match) {
      return null;
    }
    return this.match.getMatchString();
  }

  hasResult(): boolean {
    return is.number(this.homeGoal) && is.number(this.guestGoal);
  }

  getMatchLink(): string | null {
    if (!this.match) {
      return null;
    }
    return this.match.getLink();
  }
}
